import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Icon from '../components/ui/Icon';
import Button from '../components/ui/Button';
import Pagination from '../components/ui/Pagination';
import { EmptyState, ErrorState } from '../components/ui/States';
import HubFilterChips from '../components/stations/HubFilterChips';
import StationsTable from '../components/stations/StationsTable';
import TaxonomySection from '../components/stations/TaxonomySection';
import FuelPriceDrawer from '../components/stations/FuelPriceDrawer';
import useStations from '../components/stations/useStations';
import { entriesToStationFuels, groupOptions } from '../components/stations/stationAdapter';
import { MAX_SEARCH_LENGTH, PAGE_SIZES } from '../components/stations/stationsParams';
import { stationsApi } from '../api/stations';

const fmt = (n) => n.toLocaleString('en-US');

function errorView(error) {
  if (error?.status === 403) return { icon: 'lock', title: 'Admin access required', retry: false };
  if (error?.status === 429) return { icon: 'hourglass_top', title: 'Too many requests, try again shortly', retry: true, retryAfter: 5 };
  return { icon: 'cloud_off', title: "Couldn't load stations", description: error?.message, retry: true };
}

/** Gas Stations & Charging Points: station prices, filters and the add/edit price drawer. */
export default function GasStationsPage() {
  const queryClient = useQueryClient();
  const {
    params,
    setParams,
    searchInput,
    setSearchInput,
    clearFilters,
    stations,
    hubCounts,
    googleAvailable,
    pagination,
    isInitialLoading,
    isRefreshing,
    error,
    refetch,
  } = useStations();

  const optionsQuery = useQuery({
    queryKey: ['fuelOptions'],
    queryFn: ({ signal }) => stationsApi.getFuelOptions(undefined, { signal }),
    staleTime: 5 * 60_000,
  });
  const options = optionsQuery.data ?? [];
  const groups = useMemo(() => groupOptions(options), [options]);

  /** @type {[{ mode: 'add'|'edit', station?: object, fuelOptionId?: number } | null, Function]} */
  const [drawer, setDrawer] = useState(null);

  // Update the row right away from the save/reset response, then refetch counts and order
  const handleSaved = (placeId, fuelsWithEntries) => {
    const fuels = entriesToStationFuels(fuelsWithEntries);
    queryClient.setQueriesData({ queryKey: ['stations'] }, (old) =>
      old?.stations ? { ...old, stations: old.stations.map((s) => (s.placeId === placeId ? { ...s, fuels } : s)) } : old,
    );
    queryClient.invalidateQueries({ queryKey: ['stations'] });
    queryClient.invalidateQueries({ queryKey: ['stationFuels', placeId] });
  };

  const total = pagination?.totalItems ?? 0;
  const hasFilters = Boolean(params.search) || params.hubType !== 'all';
  const from = total ? (params.page - 1) * params.limit + 1 : 0;
  const to = Math.min(params.page * params.limit, total);

  let body;
  if (error && !isInitialLoading) {
    const view = errorView(error);
    body = (
      <ErrorState
        icon={view.icon}
        title={view.title}
        description={view.description}
        retryAfter={view.retryAfter}
        onRetry={view.retry ? () => refetch() : undefined}
      />
    );
  } else if (!isInitialLoading && pagination && stations.length === 0) {
    body = (
      <EmptyState
        tone="neutral"
        icon="local_gas_station"
        title="No stations found"
        description={hasFilters ? 'Nothing matches the current search or filter.' : 'Stations appear here once their first fuel price is saved.'}
        action={
          hasFilters ? (
            <Button variant="secondary" icon="filter_alt_off" onClick={clearFilters}>
              Clear filters
            </Button>
          ) : (
            <Button icon="add_circle" onClick={() => setDrawer({ mode: 'add' })}>
              Add Fuel Price
            </Button>
          )
        }
      />
    );
  } else {
    body = (
      <StationsTable
        stations={stations}
        loading={isInitialLoading}
        refreshing={isRefreshing}
        skeletonCount={Math.min(params.limit, 6)}
        onEdit={(station) => setDrawer({ mode: 'edit', station })}
        onEditFuel={(station, fuelOptionId) => setDrawer({ mode: 'edit', station, fuelOptionId })}
      />
    );
  }

  return (
    <>
      <section className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-primary tracking-tight">Gas Stations &amp; Charging Points</h1>
          <p className="mt-1 text-sm text-on-surface-variant">
            Manage station listings, fuel options, EV charge points, and real-time pricing.
          </p>
        </div>
        <Button size="lg" icon="add_circle" onClick={() => setDrawer({ mode: 'add' })} disabled={!options.length} className="shrink-0">
          Add Fuel Price
        </Button>
      </section>

      {!googleAvailable && (
        <div role="status" className="flex items-start gap-2 rounded-xl border border-status-pending-border bg-status-pending-bg px-4 py-3 text-sm text-status-pending-text">
          <Icon name="info" size={18} />
          Station names are temporarily unavailable from Google — prices are still shown.
        </div>
      )}

      <section className="bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs p-4 flex flex-col gap-3">
        <div className="relative max-w-xl" role="search">
          <label htmlFor="stations-search" className="sr-only">
            Search stations
          </label>
          <Icon name="search" size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-on-surface-variant pointer-events-none" />
          <input
            id="stations-search"
            type="search"
            autoComplete="off"
            maxLength={MAX_SEARCH_LENGTH}
            placeholder="Search by station name, brand, placeId or city…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full h-10 pl-9 pr-3 text-[13px] bg-surface-container-low border border-transparent rounded-lg text-on-surface placeholder:text-on-surface-variant/60 outline-none transition focus:bg-surface-container-lowest focus:border-primary-container focus:shadow-[0_0_0_3px_rgba(34,197,94,0.15)]"
          />
        </div>
        <HubFilterChips value={params.hubType} counts={hubCounts} onChange={(hubType) => setParams({ hubType })} />
      </section>

      <section className="relative bg-surface-container-lowest rounded-xl border border-outline-variant/30 shadow-xs overflow-hidden">
        <p className="sr-only" role="status" aria-live="polite">
          {isInitialLoading || isRefreshing || !pagination ? '' : `${fmt(total)} ${total === 1 ? 'station' : 'stations'} found`}
        </p>
        {isRefreshing && (
          <div className="absolute top-2 right-3 z-10 inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-surface-container-lowest/90 shadow-xs text-[11px] text-on-surface-variant">
            <Icon name="progress_activity" size={14} className="animate-spin text-secondary" />
            Loading…
          </div>
        )}
        {body}
        {!error && pagination && total > 0 && (
          <Pagination
            page={params.page}
            pageSize={params.limit}
            total={total}
            onPageChange={(page) => setParams({ page })}
            pageSizeOptions={PAGE_SIZES}
            onPageSizeChange={(limit) => setParams({ limit })}
            disabled={isRefreshing}
            summary={
              <span>
                Showing <strong className="font-data-mono text-on-surface">{fmt(from)}</strong> to{' '}
                <strong className="font-data-mono text-on-surface">{fmt(to)}</strong> of{' '}
                <strong className="font-data-mono text-on-surface">{fmt(total)}</strong> records
              </span>
            }
          />
        )}
      </section>

      <TaxonomySection groups={groups} loading={optionsQuery.isPending} error={optionsQuery.error} />

      {drawer && options.length > 0 && (
        <FuelPriceDrawer
          key={`${drawer.mode}-${drawer.station?.placeId ?? 'new'}-${drawer.fuelOptionId ?? ''}`}
          mode={drawer.mode}
          station={drawer.station}
          initialFuelOptionId={drawer.fuelOptionId}
          options={options}
          onClose={() => setDrawer(null)}
          onSaved={handleSaved}
        />
      )}
    </>
  );
}
